import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild,
} from "@angular/core";
import {
	AbstractControl,
	UntypedFormArray,
	UntypedFormBuilder,
	UntypedFormControl,
	UntypedFormGroup,
	ValidatorFn,
	Validators,
} from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatExpansionPanelHeader } from "@angular/material/expansion";
import { ActivatedRoute, Router } from "@angular/router";
import { isEmpty } from "lodash";
import moment from "moment";
import { Subscription } from "rxjs";

import { CONSTANT } from "src/app/constant";
import { RouteType } from "src/app/models/enum";
import {
	FormDataOptions,
	Observation,
	SessionCfg,
	SessionCfgRaw,
	SessionGroup,
	TS1Form,
	TS1FormRequestJSON,
} from "src/app/models/models";

import { CdkDragDrop } from "@angular/cdk/drag-drop";
import { CbtaModalComponent } from "src/app/shared/components/cbta-modal/cbta-modal.component";
import { HasUnsavedFormChanges } from "src/app/shared/interfaces/has-unsaved-form-changes";
import {
	ErrorSnackbarComponent,
	ErrorSnackbarData,
} from "src/app/shared/components/cbta-snackbar/error-snackbar.component";
import { resolveHttpErrorMessageText } from "src/app/shared/utils/http-error-message";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { RoleService } from "src/app/shared/services/role.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { CustomValidationService } from "../../services/custom-validation.service";
import {
	buildAdminFormSessionPayload,
	buildManeuverV2FromObservations,
	buildSessionRemarksByCode,
	buildTs1SessionColumns,
	buildV2RequestPayload,
	filterTs1ManeuverOptions,
	isEts35FormType,
	isTs1FormType,
	parseFormConfigResponse,
	sortSessionsAlphabetically,
	syncTs1SessionRemarkControls,
	wireTs1ReminderNotesClearing,
	wireTs1SessionCheckboxRemarkClearing,
} from "../ts1-v2/ts1-form.factories";
import {
	buildEts1SessionColumnTree,
	Ets1SessionColumnVM,
} from "src/app/cbta-web/ets1/session/ets1-session-layout.utils";
import {
	hasEts1RadioRequiredErrors,
	hasSessionRemarkRequiredErrors,
	markMissingRequiredEts1RadioSelection,
	missingRequiredSessionText,
} from "src/app/cbta-web/ets1/session/ets1-session-validation.utils";
import {
	mapApiRowsToEts1SessionConfig,
	syncEts1SessionFormControls,
} from "src/app/cbta-web/ets1/session/ets1-session-form.utils";
import {
	buildEts35SessionSections,
	Ets35SessionSectionVM,
	ets35TrainingColumns,
	syncEts35AdminSessionControls,
} from "src/app/cbta-web/ets1/session/ets35-session-sections.utils";

@Component({
	selector: "app-create-form",
	templateUrl: "./create-form.component.html",
	styleUrls: ["./create-form.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CreateFormComponent implements OnInit, OnDestroy, HasUnsavedFormChanges {
	createNewForm!: UntypedFormGroup;

	today = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate(), 0, 0, 0, 0);

	@ViewChild("dateStartInput") dateStartInput?: ElementRef;
	@ViewChild("dateEndInput") dateEndInput?: ElementRef;

	// sessions UI state
	sessionGroups: SessionGroup[] = [];
	singleCols: SessionCfg[][] = [];
	ts1SessionCols: SessionCfg[][] = [];
	ets1SessionColumns: Ets1SessionColumnVM[] = [];
	ets35SessionSections: Ets35SessionSectionVM[] = [];
	twoColsLeft?: SessionGroup;
	twoColsRight: SessionCfg[][] = [];
	secondGroupTitle = "";
	lastColTitle = "";
	twoGroupsCompact = false;
	lastCols?: SessionCfg[][] = [];

	lastObservations: Observation[] = [];
	fixedManeuvers: any[] = [];
	ts1FormManeuversOptions: FormDataOptions[] = [];

	formSuccess = false;
	formErrorMessage = "";
	formConfigReady = false;
	DATE_FORMAT = CONSTANT.DATE_FORMAT;
	private readonly DISPLAY_FMT = this.DATE_FORMAT.INPUT_DISPLAY_DATE_FORMAT;

	private notReqSub?: Subscription;
	private otherSessionSubs: Subscription[] = [];
	private sessionRemarkSubs: Subscription[] = [];
	private ets1SessionSubs: Subscription[] = [];
	private reminderNotesSubs: Subscription[] = [];
	private formConfigSub?: Subscription;
	private loadedFormTypeCode: string | null = null;
	private loadingFormTypeCode: string | null = null;

	// Keys that are not OB categories on a maneuver FG
	private readonly BASE_OB_KEYS = new Set([
		"formType",
		"maneuver",
		"maneuverCode",
		"assessmentId",
		"traineeAssessmentId",
		"candidateId",
		"selectedOBs",
		"safetyOutcome",
		"repeat",
	]);

	constructor(
		private fb: UntypedFormBuilder,
		private httpService: HttpService,
		private cd: ChangeDetectorRef,
		private loadingService: LoaderService,
		public sharedService: SharedService,
		private route: ActivatedRoute,
		private router: Router,
		private customValidationService: CustomValidationService,
		private roleService: RoleService,
		private dialog: MatDialog,
		private snackBar: MatSnackBar,
	) {
		this.initCreateNewForm();
	}

	scrollToTop(): void {
		window.scrollTo({ top: 0, behavior: "smooth" });
	}

	ngOnInit(): void {
		this.sharedService.setActivePage(RouteType.FORMS);

		// keep maneuver validators in sync with formType at all times
		this.formType.valueChanges.subscribe(() => {
			this.refreshManeuverValidatorsForFormType();
			// touch & revalidate so errors clear immediately when switching away from TS-1
			this.maneuvers.markAllAsTouched();
			this.maneuvers.updateValueAndValidity({ onlySelf: false });
			this.cd.markForCheck();
		});
	}

	ngOnDestroy(): void {
		this.teardownSessionSubscriptions();
		this.formConfigSub?.unsubscribe();
	}

	// ---------- Validators ----------
	private requireAtLeastOneSession(): ValidatorFn {
		return (ctrl: AbstractControl) => {
			const g = ctrl as UntypedFormGroup;
			const hasOne = Object.values(g.controls).some((c: any) => c?.value === true);
			return hasOne ? null : { noSessionSelected: true };
		};
	}

	// ---------- Init ----------
	private initCreateNewForm(): void {
		this.createNewForm = this.fb.group({
			formType: ["", [Validators.required]],
			formName: ["", [Validators.required]],
			syllabusStartDate: [new Date(), [Validators.required]],
			syllabusEndDate: [new Date(), [Validators.required]],
			sessions: this.fb.group({}),
			sessionRemarks: this.fb.group({}),
			ets1SessionVersion: [2],
			reminderNotesEnabled: [false],
			reminderNotesTemplate: [""],
			reminderNotes: this.fb.array([]),
			maneuvers: this.fb.array([]),
		});

		this.sessions.addValidators(this.requireAtLeastOneSession());
	}

	// ---------- Getters ----------
	get formType() {
		return this.createNewForm.get("formType")!;
	}
	get formName() {
		return this.createNewForm.get("formName")!;
	}
	get syllabusStartDate() {
		return this.createNewForm.get("syllabusStartDate")! as UntypedFormControl;
	}
	get syllabusEndDate() {
		return this.createNewForm.get("syllabusEndDate")! as UntypedFormControl;
	}
	get sessions(): UntypedFormGroup {
		return this.createNewForm.get("sessions") as UntypedFormGroup;
	}
	get sessionRemarks(): UntypedFormGroup {
		return this.createNewForm.get("sessionRemarks") as UntypedFormGroup;
	}
	get reminderNotesEnabled(): UntypedFormControl {
		return this.createNewForm.get("reminderNotesEnabled") as UntypedFormControl;
	}
	get reminderNotesTemplate(): UntypedFormControl {
		return this.createNewForm.get("reminderNotesTemplate") as UntypedFormControl;
	}
	get reminderNotes(): UntypedFormArray {
		return this.createNewForm.get("reminderNotes") as UntypedFormArray;
	}
	get maneuvers(): UntypedFormArray {
		return this.createNewForm.get("maneuvers") as UntypedFormArray;
	}

	get hasSessionRemarkRequiredErrors(): boolean {
		if (this.isEts35SessionCatalog) {
			return hasSessionRemarkRequiredErrors(this.sessionRemarks);
		}
		return this.isTS1 && hasSessionRemarkRequiredErrors(this.sessionRemarks);
	}

	get hasEts1RadioRequiredErrors(): boolean {
		if (this.isEts35SessionCatalog) {
			return hasEts1RadioRequiredErrors(this.sessions);
		}
		return this.isTS1 && hasEts1RadioRequiredErrors(this.sessions);
	}

	get isTS1(): boolean {
		return isTs1FormType(this.formType.value as FormDataOptions | string | null);
	}

	get isEts35SessionCatalog(): boolean {
		return isEts35FormType(this.formType.value as FormDataOptions | string | null) && this.ets35SessionSections.length > 0;
	}
	get isTS6(): boolean {
		const v = this.formType.value as FormDataOptions | null;
		return !!v && v.code === CONSTANT.FORM_TYPE.ts_6;
	}
	get isCabinCrew(): boolean {
		const v = this.formType.value as FormDataOptions | null;
		return !!v && v.code === CONSTANT.FORM_TYPE.cabin_crew;
	}
	get isCabinCrewInstructor(): boolean {
		const v = this.formType.value as FormDataOptions | null;
		return !!v && v.code === CONSTANT.FORM_TYPE.cabin_crew_instructor;
	}

	// convenience getters for dates in template
	get trainingStartDate() {
		return moment(this.syllabusStartDate.value).toDate();
	}
	get trainingEndDate() {
		return moment(this.syllabusEndDate.value).toDate();
	}

	// ---------- Sessions helpers ----------
	private splitIntoColumns<T>(items: T[], columns: number): T[][] {
		if (columns <= 0) return [];
		if (columns === 1) return [items.slice()];

		if (columns === 2) {
			const n = items.length;
			// Even-ish split, but first column must have at least 5 (if available)
			const firstCount = Math.min(n, Math.max(5, Math.ceil(n / 2)));
			return [items.slice(0, firstCount), items.slice(firstCount)];
		}

		// Fallback for 3+ columns (simple round-robin or keep your previous logic)
		const cols: T[][] = Array.from({ length: columns }, () => []);
		items.forEach((item, idx) => cols[idx % columns].push(item));
		return cols;
	}

	private teardownSessionSubscriptions() {
		if (this.notReqSub) {
			this.notReqSub.unsubscribe();
			this.notReqSub = undefined;
		}
		this.otherSessionSubs.forEach((s) => s.unsubscribe());
		this.otherSessionSubs = [];
		this.sessionRemarkSubs.forEach((s) => s.unsubscribe());
		this.sessionRemarkSubs = [];
		this.reminderNotesSubs.forEach((s) => s.unsubscribe());
		this.reminderNotesSubs = [];
		this.ets1SessionSubs.forEach((s) => s.unsubscribe());
		this.ets1SessionSubs = [];
	}

	private wireUpNotRequiredMutualExclusion() {
		this.teardownSessionSubscriptions();

		const notReq = this.sessions.get("not_required") as UntypedFormControl | null;
		if (!notReq) return;

		this.notReqSub = notReq.valueChanges.subscribe((val: boolean) => {
			if (val !== true) return;
			Object.keys(this.sessions.controls).forEach((k) => {
				if (k === "not_required") return;
				const c = this.sessions.get(k) as UntypedFormControl;
				if (c?.value === true) c.setValue(false, { emitEvent: false });
			});
			this.sessions.updateValueAndValidity({ onlySelf: true, emitEvent: false });
		});

		Object.keys(this.sessions.controls).forEach((k) => {
			if (k === "not_required") return;
			const c = this.sessions.get(k) as UntypedFormControl;
			if (!c) return;

			const sub = c.valueChanges.subscribe((val: boolean) => {
				if (val === true && notReq.value === true) {
					notReq.setValue(false, { emitEvent: false });
					this.sessions.updateValueAndValidity({ onlySelf: true, emitEvent: false });
				}
			});
			this.otherSessionSubs.push(sub);
		});
	}

	private rebuildEts1AocSessionControls(cfgs: SessionCfgRaw[]): void {
		this.teardownSessionSubscriptions();
		Object.keys(this.sessions.controls).forEach((k) => this.sessions.removeControl(k));
		Object.keys(this.sessionRemarks.controls).forEach((k) => this.sessionRemarks.removeControl(k));
		const catalog = mapApiRowsToEts1SessionConfig(cfgs);
		this.ets1SessionColumns = buildEts1SessionColumnTree(catalog);
		syncEts1SessionFormControls(
			this.fb,
			this.sessions,
			this.sessionRemarks,
			this.ets1SessionColumns,
			this.ets1SessionSubs,
		);
		this.sessionGroups = [];
		this.ts1SessionCols = [];
		this.ets35SessionSections = [];
		this.sessions.updateValueAndValidity({ onlySelf: true });
	}

	private rebuildEts35SessionControls(cfgs: SessionCfgRaw[]): void {
		this.teardownSessionSubscriptions();
		Object.keys(this.sessions.controls).forEach((key) => this.sessions.removeControl(key));
		Object.keys(this.sessionRemarks.controls).forEach((key) => this.sessionRemarks.removeControl(key));
		this.ets35SessionSections = buildEts35SessionSections(cfgs);
		syncEts35AdminSessionControls(
			this.fb,
			this.sessions,
			this.sessionRemarks,
			this.ets35SessionSections,
			this.ets1SessionSubs,
		);
		this.ets1SessionColumns = [];
		this.sessionGroups = [];
		this.ts1SessionCols = [];
		this.sessions.updateValueAndValidity({ onlySelf: true });
	}

	private rebuildSessionControls(cfgs: SessionCfgRaw[], ts1FlatLayout = false) {
		// clear controls and subs
		this.teardownSessionSubscriptions();
		Object.keys(this.sessions.controls).forEach((k) => this.sessions.removeControl(k));
		Object.keys(this.sessionRemarks.controls).forEach((k) => this.sessionRemarks.removeControl(k));
		this.ts1SessionCols = [];

		if (ts1FlatLayout) {
			const isOthers = (s: SessionCfgRaw) => s.session_conf_type?.code?.toLowerCase() === "other";
			const ts1Sessions = cfgs.filter((s) => !isOthers(s));
			const items = sortSessionsAlphabetically(ts1Sessions);
			items.forEach((item) => this.sessions.addControl(item.code, new UntypedFormControl(false)));
			syncTs1SessionRemarkControls(
				this.fb,
				this.sessionRemarks,
				items.map((i) => i.code),
			);
			wireTs1SessionCheckboxRemarkClearing(this.sessions, this.sessionRemarks, this.sessionRemarkSubs);

			this.ts1SessionCols = buildTs1SessionColumns(ts1Sessions);
			this.sessionGroups = [];
			this.singleCols = [];
			this.twoColsLeft = undefined;
			this.twoColsRight = [];
			this.twoGroupsCompact = false;
			this.secondGroupTitle = "";
			this.lastCols = [];
			this.lastColTitle = "";

			this.sessions.updateValueAndValidity({ onlySelf: true });
			return;
		}

		// group and add controls
		const grouped = new Map<string, SessionCfg[]>();
		const groupedCode = new Map<string, string>();

		cfgs.forEach((raw) => {
			const key = raw.session_conf_type?.description || "Others";
			const gCode = raw.session_conf_type?.code || "others";
			if (!grouped.has(key)) grouped.set(key, []);
			grouped.get(key)!.push({ code: raw.code, description: raw.description });
			if (!groupedCode.has(key)) groupedCode.set(key, gCode);

			this.sessions.addControl(raw.code, new UntypedFormControl(false));
		});

		// synthetic "not_required"
		if (!this.sessions.get("not_required")) {
			this.sessions.addControl("not_required", new UntypedFormControl(false));
		}

		// UI groups
		this.sessionGroups = Array.from(grouped.entries()).map(([key, items]) => ({
			code: groupedCode.get(key) || key.toLowerCase().replace(/\s+/g, "_"),
			key,
			items,
		}));

		// layout
		if (this.sessionGroups.length === 1) {
			this.singleCols = this.splitIntoColumns(this.sessionGroups[0].items, 4);
			this.twoColsLeft = undefined;
			this.twoColsRight = [];
			this.twoGroupsCompact = false;
			this.secondGroupTitle = "";
		} else if (this.sessionGroups.length === 4) {
			this.twoColsLeft = this.sessionGroups[0];
			const rightItems = this.sessionGroups[1].items;
			const thirdItems = this.sessionGroups[2].items;
			this.twoGroupsCompact = rightItems.length <= 4;
			this.twoColsRight = this.twoGroupsCompact ? [] : this.splitIntoColumns(rightItems, 2);
			this.singleCols = [];
			this.lastCols = this.splitIntoColumns(thirdItems, 2);
			this.lastColTitle = this.sessionGroups[2].key;
			this.secondGroupTitle = this.sessionGroups[1].key;
		} else if (this.sessionGroups.length === 3) {
			this.twoColsLeft = this.sessionGroups[0];
			const rightItems = this.sessionGroups[1].items;
			this.twoGroupsCompact = rightItems.length <= 4;
			this.twoColsRight = this.twoGroupsCompact ? [] : this.splitIntoColumns(rightItems, 2);
			this.singleCols = [];
			this.secondGroupTitle = this.sessionGroups[1].key;
		} else {
			this.singleCols = [];
			this.twoColsLeft = undefined;
			this.twoColsRight = [];
			this.twoGroupsCompact = false;
			this.secondGroupTitle = "";
		}

		this.sessions.updateValueAndValidity({ onlySelf: true });
		this.wireUpNotRequiredMutualExclusion();
	}

	// ---------- Maneuver builders ----------
	private makeObsValue() {
		return {
			totalCount: 0,
			currentCount: 0,
			preDefined: true,
			lastAction: CONSTANT.OB_LAST_ACTION.CREATE,
		};
	}

	/** IMPORTANT: only attach the validator for TS-1 */
	private buildManeuverFromObservations(observations: Observation[], formType: string): UntypedFormGroup {
		const byCategory = new Map<string, Array<{ key: string; num: number }>>();
		const seen = new Set<string>();

		for (const o of observations || []) {
			const code = (o.code || "").trim();
			if (!code || seen.has(code)) continue;
			seen.add(code);

			const m = /^([a-zA-Z]+)_(\d+)$/.exec(code);
			if (!m) continue;
			const cat = m[1].toUpperCase();
			const num = Number(m[2]);
			const key = `${cat}${num}`;
			if (!byCategory.has(cat)) byCategory.set(cat, []);
			byCategory.get(cat)!.push({ key, num });
		}

		const categoryGroups: Record<string, any> = {};
		for (const [cat, items] of byCategory.entries()) {
			items.sort((a, b) => a.num - b.num);
			const controls: Record<string, any> = {};
			for (const it of items) controls[it.key] = [{ value: this.makeObsValue(), disabled: false }];
			categoryGroups[cat] = this.fb.group(controls);
		}

		const group = this.fb.group({
			maneuver: ["", [Validators.required, this.customValidationService.validateManeuver]],
			formType: [formType],
			maneuverCode: [""],
			assessmentId: [""],
			traineeAssessmentId: [""],
			candidateId: [""],
			selectedOBs: [[]],
			safetyOutcome: [-2],
			repeat: [0],
			...categoryGroups,
		});

		return group;
	}

	/** CC/CCI: allow multi-segment categories like CC_COM_1 (same TS-1-only validator rule) */
	private buildManeuverCrewFromObservations(observations: Observation[], formType: string): UntypedFormGroup {
		const byCategory = new Map<string, Array<{ key: string; num: number }>>();
		const seen = new Set<string>();

		for (const o of observations || []) {
			const code = (o.code || "").trim();
			if (!code || seen.has(code)) continue;
			seen.add(code);

			const m = /^([a-zA-Z]+(?:_[a-zA-Z]+)*)_(\d+)$/.exec(code);
			if (!m) continue;
			const cat = m[1].toUpperCase();
			const num = Number(m[2]);
			const key = `${cat}${num}`;
			if (!byCategory.has(cat)) byCategory.set(cat, []);
			byCategory.get(cat)!.push({ key, num });
		}

		const categoryGroups: Record<string, any> = {};
		for (const [cat, items] of byCategory.entries()) {
			items.sort((a, b) => a.num - b.num);
			const controls: Record<string, any> = {};
			for (const it of items) controls[it.key] = [{ value: this.makeObsValue(), disabled: false }];
			categoryGroups[cat] = this.fb.group(controls);
		}

		const group = this.fb.group({
			maneuver: ["", [Validators.required, this.customValidationService.validateManeuver]],
			formType: [formType],
			maneuverCode: [""],
			assessmentId: [""],
			traineeAssessmentId: [""],
			candidateId: [""],
			selectedOBs: [[]],
			safetyOutcome: [-2],
			repeat: [0],
			...categoryGroups,
		});

		return group;
	}

	/** Refresh per-maneuver validators when formType changes */
	private refreshManeuverValidatorsForFormType(): void {
		for (const ctrl of this.maneuvers.controls) {
			const mfg = ctrl as UntypedFormGroup;
			mfg.updateValueAndValidity({ onlySelf: true, emitEvent: false });
		}
		this.maneuvers.updateValueAndValidity({ onlySelf: true, emitEvent: false });
	}

	// ---------- UI Actions ----------
	onFormTypeSelected(value: FormDataOptions | null): void {
		if (!value?.code) {
			this.resetFormConfigState();
			return;
		}

		// Autocomplete emits focusOut on both option select and blur — ignore duplicate loads.
		if (value.code === this.loadedFormTypeCode || value.code === this.loadingFormTypeCode) return;

		this.formType.patchValue(value, { emitEvent: false });
		this.dropdownChanged(value);
	}

	private resetFormConfigState(): void {
		this.formConfigSub?.unsubscribe();
		this.formConfigReady = false;
		this.loadedFormTypeCode = null;
		this.loadingFormTypeCode = null;
		this.maneuvers.clear();
		this.sessionGroups = [];
		this.singleCols = [];
		this.ts1SessionCols = [];
		this.ets1SessionColumns = [];
		this.ets35SessionSections = [];
		this.twoColsLeft = undefined;
		this.twoColsRight = [];
		this.twoGroupsCompact = false;
		this.secondGroupTitle = "";
		this.lastObservations = [];
		this.fixedManeuvers = [];
		this.ts1FormManeuversOptions = [];
		this.createNewForm.setControl("sessions", this.fb.group({}));
		this.sessions.addValidators(this.requireAtLeastOneSession());
		this.reminderNotesEnabled.setValue(false, { emitEvent: false });
		this.reminderNotesTemplate.setValue("", { emitEvent: false });
	}

	dropdownChanged(value: FormDataOptions): void {
		if (!value?.code) return;

		const isTs1Form = isTs1FormType(value);
		const isEts35Form = isEts35FormType(value);
		const requestedCode = value.code;

		this.formConfigSub?.unsubscribe();
		this.formConfigReady = false;
		this.loadedFormTypeCode = null;
		this.loadingFormTypeCode = requestedCode;

		// Reset UI & controls
		this.createNewForm.setControl("sessions", this.fb.group({}));
		this.sessions.addValidators(this.requireAtLeastOneSession());
		this.maneuvers.clear();
		this.sessionGroups = [];
		this.singleCols = [];
		this.ts1SessionCols = [];
		this.ets1SessionColumns = [];
		this.ets35SessionSections = [];
		this.twoColsLeft = undefined;
		this.twoColsRight = [];
		this.twoGroupsCompact = false;
		this.secondGroupTitle = "";
		this.lastObservations = [];
		this.fixedManeuvers = [];
		this.ts1FormManeuversOptions = [];

		this.formConfigSub = this.httpService
			.getFormConfig(
				value,
				isTs1Form || isEts35Form ? 2 : undefined,
				isTs1Form || isEts35Form ? this.roleService.getUserAOC() : undefined,
			)
			.subscribe({
			next: (payload: any) => {
				const currentCode = (this.formType.value as FormDataOptions | null)?.code;
				if (currentCode !== requestedCode) return;

				const {
					sessionConfigurations: sessionsRaw,
					observations: obsRaw,
					maneuvers,
				} = parseFormConfigResponse(payload);
				const observations = obsRaw as Observation[];

				const isOthers = (s: SessionCfgRaw) => s.session_conf_type?.code?.toLowerCase() === "other";

				const sessions: SessionCfgRaw[] = [...sessionsRaw.filter((s) => !isOthers(s)), ...sessionsRaw.filter(isOthers)];

				this.lastObservations = observations;

				if (isTs1Form) {
					this.rebuildEts1AocSessionControls(sessions);
					this.ts1FormManeuversOptions = filterTs1ManeuverOptions(maneuvers as FormDataOptions[]);
				} else if (isEts35Form) {
					this.rebuildEts35SessionControls(sessions);
				} else {
					this.rebuildSessionControls(sessions, false);
				}

				// TS-1 v2: maneuvers are added manually via the assessment config modal.
				if (!isTs1Form) {
					if (this.isCabinCrew || this.isCabinCrewInstructor) {
						maneuvers.forEach((m: any) => {
							const obsForM = observations.filter((o) => o.fixed_maneuver === m.code);
							const mfg = this.buildManeuverCrewFromObservations(obsForM, this.formType.value);
							this.maneuvers.push(mfg);
							this.fixedManeuvers.push(m);
							this.setManeuverOnGroup(mfg, { code: m.code, description: m.description });
						});
					} else {
						maneuvers.forEach((m: any) => {
							const obsForM = observations.filter((o) => o.fixed_maneuver === m.code);
							const mfg = this.buildManeuverFromObservations(obsForM, this.formType.value);
							this.maneuvers.push(mfg);
							this.fixedManeuvers.push(m);

							if (this.isTS6) {
								this.setManeuverOnGroup(mfg, { code: value.code, description: value.description });
							} else {
								this.setManeuverOnGroup(mfg, { code: m.code, description: m.description });
							}
						});
					}
				}

				// make sure validators match the *current* type
				this.refreshManeuverValidatorsForFormType();
				this.maneuvers.updateValueAndValidity({ emitEvent: false });

				this.loadedFormTypeCode = requestedCode;
				this.loadingFormTypeCode = null;
				this.formConfigReady = true;
				this.cd.markForCheck();
			},
			error: () => {
				if (this.loadingFormTypeCode === requestedCode) {
					this.loadingFormTypeCode = null;
				}
				this.cd.markForCheck();
			},
		});
	}

	private parseDateOnlyStrict(s: string): Date | null {
		const m = moment(s, this.DISPLAY_FMT, true);
		if (!m.isValid()) return null;
		return m.hour(12).minute(0).second(0).millisecond(0).toDate();
	}

	handleStartDateChange(dateString: string): void {
		if (isEmpty(dateString)) return;
		const start = new Date(dateString);
		if (!start) {
			this.syllabusStartDate.setErrors({ invalidDate: true });
			return;
		}

		const endVal = this.syllabusEndDate.value;
		const end = endVal instanceof Date ? new Date(endVal.getTime()) : this.parseDateOnlyStrict(String(endVal));

		this.syllabusStartDate.patchValue(start);
		if (!end || end < start) this.syllabusEndDate.patchValue(start);
	}

	handleEndDateChange(dateString: string): void {
		if (isEmpty(dateString)) return;

		const end = new Date(dateString);

		if (!end) {
			this.syllabusEndDate.setErrors({ invalidDate: true });
			return;
		}

		const startVal = this.syllabusStartDate.value;
		const start = startVal instanceof Date ? new Date(startVal.getTime()) : this.parseDateOnlyStrict(String(startVal));

		// If there's no valid start date or the selected end < start,
		// update start date to match end date
		if (!start || end <= start) {
			this.syllabusStartDate.patchValue(end);
		}

		this.syllabusEndDate.patchValue(end);
	}

	saveManeuver(index: number, value: FormDataOptions, panel: MatExpansionPanelHeader): void {
		// Only TS-1
		const control = this.maneuvers.at(index).get("maneuver");
		control?.patchValue(value);
		control?.markAsDirty();
		control && this.customValidationService.validateManeuver(control);
		if (panel && !(panel as any)._isExpanded()) (panel as any)._toggle();

		this.maneuvers.at(index).markAsTouched();
		this.maneuvers.at(index).updateValueAndValidity();
		this.maneuvers.updateValueAndValidity({ onlySelf: true });
	}

	removeManeuver(idx: number) {
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "remove-maneuver-modal",
			height: "240px",
			width: "360px",
			hasBackdrop: true,
			data: {
				modalTitle: "Confirmation",
				modalContent: "You are about to delete this Maneuver and all associated OBs. Are you sure you want to proceed?",
				modalBtnConfirmText: "Yes",
				modalBtnCancelText: "No",
			},
		});

		const sub = dialogRef.afterClosed().subscribe((result: { close?: boolean }) => {
			if (result?.close === true) {
				this.confirmRemoveManeuver(idx);
			}
			sub.unsubscribe();
		});
	}

	private confirmRemoveManeuver(idx: number) {
		this.maneuvers.removeAt(idx);
		this.maneuvers.updateValueAndValidity({ onlySelf: true });
	}

	addNewManeuver() {
		if (this.isTS1) return;
		const mfg = this.buildManeuverFromObservations(this.lastObservations || [], this.formType.value);
		if (!this.isTS1) {
			const ft = this.formType.value as FormDataOptions;
			if (ft) this.setManeuverOnGroup(mfg, ft);
		}
		this.maneuvers.push(mfg);
		// ensure correct validators for current type
		this.refreshManeuverValidatorsForFormType();

		this.maneuvers.updateValueAndValidity({ onlySelf: true });
		this.cd.detectChanges();
	}

	onAddManeuverV2(selected: FormDataOptions) {
		const mfg = buildManeuverV2FromObservations(this.fb, this.lastObservations || [], this.formType.value);
		mfg.get("maneuver")?.patchValue(selected);
		mfg.get("maneuver")?.setValidators([Validators.required, this.customValidationService.validateManeuver]);
		this.maneuvers.push(mfg);
		this.maneuvers.updateValueAndValidity({ onlySelf: true });
		this.cd.detectChanges();
	}

	onRemoveManeuverV2(idx: number) {
		this.confirmRemoveManeuver(idx);
	}

	onDropManeuver(event: CdkDragDrop<any[]>) {
		if (!this.isTS1) return;

		const fa = this.maneuvers;
		const prev = event.previousIndex;
		const curr = event.currentIndex;

		if (prev === curr) return;

		// Reorder the FormArray controls
		const ctrl = fa.at(prev);
		fa.removeAt(prev);
		fa.insert(curr, ctrl);

		// Keep indexes/validators in sync and refresh the view
		fa.updateValueAndValidity({ onlySelf: true });
		this.cd.detectChanges();
	}

	// ---------- Submit ----------
	onSubmit(): void {
		this.createNewForm.updateValueAndValidity({ onlySelf: false, emitEvent: false });
		this.createNewForm.markAllAsTouched();
		this.sessions?.markAllAsTouched?.();
		this.sessionRemarks?.markAllAsTouched?.();
		this.maneuvers?.markAllAsTouched?.();

		if (this.createNewForm.pending || this.createNewForm.invalid) {
			this.focusFirstInvalidControl();
			this.scrollToTop();
			return;
		}

		if (this.isTS1 && this.maneuvers.length === 0) {
			this.scrollToTop();
			return;
		}

		const remarkColumns = this.isEts35SessionCatalog
			? ets35TrainingColumns(this.ets35SessionSections)
			: this.ets1SessionColumns;
		if (
			(this.isTS1 || this.isEts35SessionCatalog) &&
			missingRequiredSessionText(this.sessions, this.sessionRemarks, remarkColumns)
		) {
			this.scrollToTop();
			return;
		}

		if (
			(this.isTS1 || this.isEts35SessionCatalog) &&
			markMissingRequiredEts1RadioSelection(this.sessions, remarkColumns)
		) {
			this.scrollToTop();
			return;
		}

		let body: TS1FormRequestJSON;

		if (this.isTS1) {
			body = buildV2RequestPayload(
				this.createNewForm.value,
				this.roleService.getUserAOC(),
				this.DATE_FORMAT.FORMS_BE_DATE_FORMAT,
				moment,
				this.ets1SessionColumns,
			);
		} else {
			const formValue = this.createNewForm.getRawValue() as TS1Form & { sessionRemarks?: Record<string, string> };

			const sessionPayload = this.isEts35SessionCatalog
				? buildAdminFormSessionPayload(
						(formValue.sessions ?? {}) as unknown as Record<string, boolean>,
						formValue.sessionRemarks ?? {},
						this.ets35SessionSections.flatMap((section) => section.columns),
					)
				: {
						sessionDetails: Object.entries(formValue.sessions ?? {})
							.filter(([code, selected]) => code !== "not_required" && selected)
							.map(([code]) => code),
						sessionRemarksByCode: buildSessionRemarksByCode(
							formValue.sessions as unknown as Record<string, boolean>,
							formValue.sessionRemarks ?? {},
						),
					};
			const sessionCodes = sessionPayload.sessionDetails;

			// maneuvers → assessmentConfig
			type OB = { observationType: string; attempt: number };
			const baseFields = this.BASE_OB_KEYS;
			const maneuvers = Array.isArray(formValue.maneuvers) ? formValue.maneuvers : [];

			const assessmentConfig = maneuvers.reduce((acc: any[], mv: any) => {
				const selectedOBs: OB[] = [];

				Object.keys(mv ?? {}).forEach((catKey) => {
					if (baseFields.has(catKey)) return;

					const grp = mv[catKey] ?? {};
					Object.keys(grp).forEach((obKey) => {
						const c = grp[obKey];
						const m = /^([A-Z]+(?:_[A-Z]+)*)(\d+)$/.exec(obKey);
						if (m && c?.currentCount > 0) {
							selectedOBs.push({
								observationType: `${m[1].toLowerCase()}_${m[2]}`,
								attempt: c.currentCount,
							});
						}
					});
				});

				const maneuverType = typeof mv?.maneuver === "string" ? mv.maneuver : mv?.maneuver?.code;
				if (maneuverType) acc.push({ maneuverType, observations: selectedOBs });
				return acc;
			}, []);

			body = {
				name: formValue.formName,
				startDate: moment(formValue.syllabusStartDate).format(this.DATE_FORMAT.FORMS_BE_DATE_FORMAT),
				endDate: moment(formValue.syllabusEndDate).format(this.DATE_FORMAT.FORMS_BE_DATE_FORMAT),
				formType: (formValue.formType as any).code,
				sessionDetails: sessionCodes,
				...(Object.keys(sessionPayload.sessionRemarksByCode ?? {}).length > 0
					? { sessionRemarksByCode: sessionPayload.sessionRemarksByCode }
					: {}),
				assessmentConfig: assessmentConfig as any,
				aocCode: this.roleService.getUserAOC(),
				...(isEts35FormType(this.formType.value as FormDataOptions | string | null)
					? { ets1SessionVersion: 2 }
					: {}),
			};
		}

		this.formErrorMessage = "";
		this.loadingService.setLoading(true);
		this.httpService.addForm(body).subscribe({
			next: () => this.onSuccessFromSubmit(),
			error: (error) => this.onErrorFormSubmit(error),
		});
	}

	// ---------- Helpers ----------
	private focusFirstInvalidControl(): void {
		const invalid: HTMLElement | null =
			document.querySelector("[formControlName].ng-invalid") ||
			document.querySelector("[formArrayName].ng-invalid") ||
			document.querySelector("[formGroupName].ng-invalid");

		if (invalid && typeof invalid.scrollIntoView === "function") {
			invalid.scrollIntoView({ behavior: "smooth", block: "center" });
			(invalid as HTMLInputElement).focus?.();
		}
	}

	onSuccessFromSubmit() {
		this.formSuccess = true;
		this.formErrorMessage = "";
		setTimeout(() => this.router.navigate([RouteType.FORMS]), 1500);
		this.loadingService.setLoading(false);
	}

	onErrorFormSubmit(error: unknown) {
		this.formSuccess = false;
		this.formErrorMessage = resolveHttpErrorMessageText(error);
		this.snackBar.openFromComponent(ErrorSnackbarComponent, {
			data: { message: this.formErrorMessage } as ErrorSnackbarData,
			duration: 5000,
			horizontalPosition: "center",
			verticalPosition: "top",
			panelClass: ["error-snackbar"],
		});
		this.loadingService.setLoading(false);
		this.scrollToTop();
		this.cd.markForCheck();
	}

	hasUnsavedFormChanges(): boolean {
		return !this.formSuccess && this.createNewForm?.dirty === true;
	}

	goBack(): void {
		void this.router.navigate([RouteType.FORMS]);
	}

	showExpansion(maneuver: AbstractControl | null): boolean {
		if (!maneuver) return false;
		const v = (maneuver as any)?.value;
		if (!v || typeof v !== "object") return false;

		const baseFields = this.BASE_OB_KEYS;
		const remainingKeys = Object.keys(v).filter((k) => !baseFields.has(k));
		return remainingKeys.length > 1;
	}

	/** For non-TS1: set maneuver to code/desc object for header + payload */
	private setManeuverOnGroup(mfg: UntypedFormGroup, opt: { code: string; description?: string }) {
		mfg.get("maneuver")?.setValue({ code: opt.code, description: opt.description ?? "" });
		mfg.get("maneuver")?.markAsDirty();
		mfg.get("maneuver")?.updateValueAndValidity({ emitEvent: false });
	}
}
